import { WorkflowExecutionResult } from '@/components/Workflow/Preview/WorkflowFormProvider'
import { NodeExecution, WorkflowRefStrict } from 'sprites-types/api/v2/workflows'

/**
 * Workflow execution phases that represent the complete lifecycle
 */
export type WorkflowPhase =
  | 'idle' // No workflow activity
  | 'building' // Template compilation/validation phase
  | 'executing' // Active workflow execution
  | 'completed' // Successfully completed
  | 'error' // Failed with error

/**
 * Building state for template operations
 */
export interface WorkflowBuildState {
  isBuilding: boolean
  buildProgress: number // 0-100
  buildMessage: string
  buildError?: string
}

/**
 * Execution state for workflow runs
 */
export interface WorkflowExecutionState {
  isExecuting: boolean
  executionProgress: number // 0-100
  executionMessage: string
  executionError?: string
}

/**
 * Progress information for long-running operations
 */
export interface WorkflowProgress {
  percentage: number // 0-100
  message: string
  details?: string
}

/**
 * Build result for template compilation
 */
export interface WorkflowBuildResult {
  success: boolean
  buildTime?: number
  warnings?: string[]
  error?: string
}

/**
 * Unified workflow state that encompasses all workflow-related states
 */
export interface UnifiedWorkflowState {
  // Core execution state
  phase: WorkflowPhase
  isExecuting: boolean
  isBuilding: boolean

  // Progress tracking
  progress: WorkflowProgress
  buildState: WorkflowBuildState
  executionState: WorkflowExecutionState

  // Context information
  workflowRef?: string
  nodeId?: string
  selectedNodeIds?: string[]
  nodeExecutions?: Record<string, NodeExecution>

  // Results
  executionResult?: WorkflowExecutionResult
  buildResult?: WorkflowBuildResult

  // Error handling
  error?: string
  lastError?: {
    phase: WorkflowPhase
    message: string
    timestamp: number
  }
}

/**
 * Actions for updating workflow state
 */
export interface WorkflowStateActions {
  // Phase transitions
  setPhase: (phase: WorkflowPhase) => void
  resetState: () => void

  // Building operations
  startBuilding: (message?: string) => void
  updateBuildProgress: (progress: number, message?: string) => void
  completeBuild: (buildResult: WorkflowBuildResult) => void
  errorBuild: (error: string) => void

  // Execution operations
  startExecution: (workflowRef: WorkflowRefStrict, message?: string) => void
  updateExecutionProgress: (progress: number, message?: string) => void
  completeExecution: (executionResult: WorkflowExecutionResult) => void
  errorExecution: (error: string) => void

  // Context updates
  updateContext: (
    context: Partial<
      Pick<UnifiedWorkflowState, 'workflowRef' | 'nodeId' | 'selectedNodeIds' | 'nodeExecutions'>
    >
  ) => void

  // Node execution updates
  updateNodeExecution: (executionId: string, execution: NodeExecution) => void
  clearNodeExecutions: () => void
}

/**
 * Complete workflow state manager interface
 */
export interface WorkflowStateManager extends UnifiedWorkflowState, WorkflowStateActions {
  // Computed states for backward compatibility
  hasActiveWorkflowExecutions: boolean
  showThinkingAnimation: boolean

  // Utility methods
  isIdle: boolean
  isActive: boolean
  canExecute: boolean
}

/**
 * Initial state for workflow manager
 */
export const initialWorkflowState: UnifiedWorkflowState = {
  phase: 'idle',
  isExecuting: false,
  isBuilding: false,

  progress: {
    percentage: 0,
    message: '',
  },

  buildState: {
    isBuilding: false,
    buildProgress: 0,
    buildMessage: '',
  },

  executionState: {
    isExecuting: false,
    executionProgress: 0,
    executionMessage: '',
  },

  nodeExecutions: {},
}

/**
 * Event types for workflow state changes
 */
export type WorkflowStateEvent =
  | { type: 'PHASE_CHANGE'; payload: { phase: WorkflowPhase } }
  | { type: 'BUILD_START'; payload: { message?: string } }
  | { type: 'BUILD_PROGRESS'; payload: { progress: number; message?: string } }
  | { type: 'BUILD_COMPLETE'; payload: { result: WorkflowBuildResult } }
  | { type: 'BUILD_ERROR'; payload: { error: string } }
  | { type: 'EXECUTION_START'; payload: { workflowRef: WorkflowRefStrict; message?: string } }
  | { type: 'EXECUTION_PROGRESS'; payload: { progress: number; message?: string } }
  | { type: 'EXECUTION_COMPLETE'; payload: { result: WorkflowExecutionResult } }
  | { type: 'EXECUTION_ERROR'; payload: { error: string } }
  | {
      type: 'CONTEXT_UPDATE'
      payload: Partial<
        Pick<UnifiedWorkflowState, 'workflowRef' | 'nodeId' | 'selectedNodeIds' | 'nodeExecutions'>
      >
    }
  | { type: 'NODE_EXECUTION_UPDATE'; payload: { executionId: string; execution: NodeExecution } }
  | { type: 'RESET' }

/**
 * Options for workflow state manager
 */
export interface WorkflowStateManagerOptions {
  // Enable debug logging
  debug?: boolean

  // Custom event listeners
  onStateChange?: (state: UnifiedWorkflowState, event: WorkflowStateEvent) => void
  onPhaseChange?: (phase: WorkflowPhase, previous: WorkflowPhase) => void
  onError?: (error: string, phase: WorkflowPhase) => void

  // Persistence options
  persistState?: boolean
  storageKey?: string
}
